import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/format';

/**
 * 공용 UI — 명세서 §7.1~§7.3, §7.6
 * 미니멀 · 라운드 · 파스텔 · 여백 · 다정함. 화려한 그라데이션과 날카로운 모서리를 쓰지 않는다.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-strong active:bg-primary-strong',
  // 피치 = 학부모 액션 (§7.2)
  secondary: 'bg-secondary text-ink hover:brightness-95 active:brightness-90',
  ghost: 'bg-transparent text-ink hover:bg-primary-soft',
  outline: 'bg-surface text-ink border border-line-strong hover:bg-primary-soft',
  // notice 레드는 삭제 확인 등에만 최소로 쓴다 (§7.2)
  danger: 'bg-surface text-notice border border-notice/40 hover:bg-notice/10',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-sm',
  md: 'h-touch px-4 text-base rounded-sm',
  lg: 'h-12 px-5 text-lg rounded-md',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, block, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        // whitespace-nowrap: 좁은 화면에서 "추가"가 "추/가"로 꺾이는 현상 방지 — 버튼 글자는 절대 줄바꿈하지 않는다
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors',
        'disabled:opacity-45 disabled:cursor-not-allowed active:scale-[0.985]',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : icon}
      {children}
    </button>
  );
});

export function Card({
  className,
  children,
  as: Tag = 'div',
  ...rest
}: {
  className?: string;
  children: ReactNode;
  as?: 'div' | 'section' | 'article' | 'li';
} & React.HTMLAttributes<HTMLElement>) {
  return (
    <Tag className={cn('card p-4', className)} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionTitle({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    // mt-5: 제목은 앞 블록보다 자기 내용(아래)에 가까워야 한다.
    // gap-* 부모(gap-0·gap-x-* 포함)는 "간격은 부모가 책임진다"로 보고 mt를 끈다 —
    // 좁은 gap 부모에 놓으면 도로 붙으니, 섹션 묶음 컨테이너는 gap-4 이상을 쓸 것.
    <div className="mt-5 flex items-center justify-between px-1 pb-2 pt-1 first:mt-0 [[class*='gap-']>&]:mt-0">
      <h2 className="text-lg font-bold">{children}</h2>
      {action}
    </div>
  );
}

/** 설정 토글 — 그린 축(§7.2), 켜짐/꺼짐이 색과 위치로 함께 드러난다 */
export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-7 w-12 shrink-0 rounded-full transition-colors',
        checked ? 'bg-primary' : 'bg-line-strong',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 h-6 w-6 rounded-full bg-surface shadow-card transition-all',
          checked ? 'left-[22px]' : 'left-0.5',
        )}
      />
    </button>
  );
}

type ChipTone = 'primary' | 'secondary' | 'accent' | 'info' | 'neutral';

const CHIP_TONES: Record<ChipTone, string> = {
  primary: 'bg-primary-soft text-primary-strong',
  secondary: 'bg-secondary-soft text-ink',
  accent: 'bg-accent-soft text-ink',
  info: 'bg-info-soft text-ink',
  // 웜 화이트 카드 위에서 옅은 테두리는 묻힌다 — 선택지 칩은 강한 경계선을 쓴다
  neutral: 'bg-bg text-ink-sub border border-line-strong',
};

export function Chip({
  tone = 'neutral',
  children,
  className,
  onClick,
  selected,
}: {
  tone?: ChipTone;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
}) {
  const base = cn(
    'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap',
    CHIP_TONES[tone],
    selected && 'ring-2 ring-primary ring-offset-1 ring-offset-surface',
    className,
  );

  if (!onClick) return <span className={base}>{children}</span>;

  return (
    <button
      type="button"
      onClick={(event) => {
        // 스크롤 줄 가장자리에 걸친 칩을 누르면 ring까지 온전히 보이게 끌어온다
        event.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
        onClick();
      }}
      className={cn(base, 'transition-all active:scale-95')}
    >
      {children}
    </button>
  );
}

/** 뱃지는 그린·피치로 (§7.1 — 빨간 뱃지 다수 회피) */
export function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="tnum inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary px-1.5 text-[11px] font-bold text-ink">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    // 항목 사이 여백은 컴포넌트가 책임진다 — 라벨이 위 항목의 입력칸과 붙으면
    // 어느 항목의 제목인지 헷갈린다. gap 컨테이너 안에서는 이중 여백을 막는다.
    <label className="mb-3 block [[class*='gap-']>&]:mb-0">
      <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
        {label}
        {required && <span className="text-primary">*</span>}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-sm text-notice">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-sm text-ink-sub">{hint}</span>
      ) : null}
    </label>
  );
}

/**
 * 입력 컨트롤이 하나가 아닌 묶음(칩 그룹·서명 패드 등)의 라벨.
 *
 * `Field`는 `<label>`이라 **첫 번째 labelable 자손으로 클릭을 넘긴다.** 칩 여러 개나
 * 캔버스+버튼을 그 안에 넣으면, 빈 여백이나 캔버스를 눌렀을 때 엉뚱한 버튼이 눌린다.
 * (실제로 서명 패드에서 캔버스를 그릴 때마다 "다시 서명"이 눌려 서명이 지워졌다.)
 * 묶음에는 `<label>`이 아니라 `role="group"`이 맞다.
 */
export function FieldGroup({
  label,
  hint,
  children,
  required,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div role="group" aria-label={label} className="mb-3 block [[class*='gap-']>&]:mb-0">
      <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
        {label}
        {required && <span className="text-primary">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-sm text-ink-sub">{hint}</span>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          // 글자 크기는 index.css의 폼 컨트롤 16px 규칙을 따른다 — 더 작게 덮으면 iOS가 포커스 줌을 한다
          'h-touch w-full rounded-sm border border-line-strong bg-surface px-3.5',
          'placeholder:text-ink-sub focus:border-primary focus:outline-none',
          className,
        )}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          'w-full rounded-sm border border-line-strong bg-surface p-3.5 leading-relaxed',
          'placeholder:text-ink-sub focus:border-primary focus:outline-none',
          className,
        )}
        {...rest}
      />
    );
  },
);

/** §7.5 — 빈 화면은 안내가 아닌 행동 유도로 */
export function EmptyState({
  title,
  body,
  action,
  illustration,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  illustration?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-full bg-primary-soft text-primary-strong">
        {illustration ?? <SproutMark />}
      </div>
      <p className="text-lg font-bold">{title}</p>
      {body && <p className="max-w-xs text-base text-ink-sub">{body}</p>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-ink-sub">
      <Loader2 className="animate-spin" size={22} />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="mx-4 my-6 rounded-md border border-line bg-surface p-4 text-center">
      <p className="text-base text-ink">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          다시 시도
        </Button>
      )}
    </div>
  );
}

/** 구독 만료 안내 배너 (§5.21 만료 정책) */
export function ReadOnlyBanner({ message }: { message: string }) {
  return (
    <div className="mx-4 mt-3 rounded-sm bg-accent-soft px-4 py-3 text-sm text-ink">{message}</div>
  );
}

export function Avatar({
  name,
  size = 40,
  photoUrl,
}: {
  name: string;
  size?: number;
  photoUrl?: string | null;
}) {
  return (
    <div
      className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-primary-soft font-bold text-primary-strong"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        name.slice(-2)
      )}
    </div>
  );
}

function SproutMark() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden>
      <path d="M15 24V13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M15 14c-6 0-9-3-9-8 5 0 9 3 9 8Z" fill="currentColor" opacity="0.75" />
      <path d="M15 16c6 0 9-3 9-8-5 0-9 3-9 8Z" fill="currentColor" opacity="0.45" />
    </svg>
  );
}
